// Asks before anything leaves the drive: to Trash, where it can still be put back for 30 days, or
// out of Trash for good, where it can't.
//
// Deleting for good only queues the purge — the API clears storage on its own time — so what was
// purged is taken out of the cached Trash here, rather than asking for Trash again and finding it
// still there.
import PropTypes from 'prop-types'
import {useMutation} from '@apollo/client/react'

import DeleteForeverIcon from '@mui/icons-material/DeleteForeverOutlined'
import Dialog from '@mui/material/Dialog'
import TrashIcon from '@mui/icons-material/DeleteOutlineRounded'

import {BaseButton} from '../shared/blocks/Buttons'
import {Text} from '../shared/blocks/Texts'

import {FOLDERS} from '../../graphql/queries/folders'
import {LOCKED} from '../../graphql/queries/locked'
import {PURGE_ITEMS, TRASH_ITEMS} from '../../graphql/mutations/trash'
import {STORAGE} from '../../graphql/queries/storage'
import {TRASH} from '../../graphql/queries/trash'
import {UPLOAD_LISTINGS} from '../../graphql/queries/uploads'

import './trashDialog.scss'

export const itemsInput = entries => ({
  folderIds: entries.filter(({kind}) => kind === 'folder').map(({id}) => id),
  uploadIds: entries.filter(({kind}) => kind === 'file').map(({id}) => id)
})

const plural = count => `${count} item${count === 1 ? '' : 's'}`

const dropPurged = (cache, {data: {purgeItems}}) => cache.updateQuery({query: TRASH}, data => data && {
  trash: {
    ...data.trash,
    folders: data.trash.folders.filter(({id}) => !purgeItems.folderIds.includes(id)),
    uploads: data.trash.uploads.filter(({id}) => !purgeItems.uploadIds.includes(id))
  }
})

const MODES = {
  trash: {
    Icon: TrashIcon,
    title: 'Move to Trash',
    body: count => `${plural(count)} will sit in Trash for 30 days. You can put ${count === 1 ? 'it' : 'them'} back until then.`,
    mutation: TRASH_ITEMS,
    options: {refetchQueries: [FOLDERS, ...UPLOAD_LISTINGS, TRASH, LOCKED]}
  },
  purge: {
    Icon: DeleteForeverIcon,
    title: 'Delete forever',
    body: count => `${plural(count)} will be deleted for good. This can’t be undone.`,
    mutation: PURGE_ITEMS,
    // Trash still takes up space, so only purging gives any back
    options: {update: dropPurged, refetchQueries: [STORAGE, LOCKED]}
  }
}

const TrashDialog = ({mode, entries, onClose, onDone}) => {
  const {Icon, title, body, mutation, options} = MODES[mode]
  const [remove, {loading, error}] = useMutation(mutation, options)

  const confirm = () => {
    remove({variables: {input: itemsInput(entries)}})
      .then(onDone)
      // Shown from the mutation's own error state below.
      .catch(() => {})
  }

  return (
    <Dialog open onClose={onClose} aria-labelledby='trash-dialog-title' slotProps={{paper: {className: 'trash-dialog'}}}>
      <div className='dialog-head'>
        <Icon />
        <h2 className='dialog-title' id='trash-dialog-title'>{title}</h2>
      </div>
      <Text className='dialog-body'>{body(entries.length)}</Text>
      {error && <Text className='dialog-error' role='alert'>That couldn’t be done. Try again.</Text>}

      <div className='dialog-actions'>
        <BaseButton className='dialog-cancel' variant='text' onClick={onClose}>Cancel</BaseButton>
        <BaseButton className='dialog-confirm' variant='text' color='error' disabled={loading} onClick={confirm}>{title}</BaseButton>
      </div>
    </Dialog>
  )
}

TrashDialog.propTypes = {
  mode: PropTypes.oneOf(['trash', 'purge']).isRequired,
  entries: PropTypes.arrayOf(PropTypes.shape({
    kind: PropTypes.oneOf(['folder', 'file']).isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  })).isRequired,
  onClose: PropTypes.func.isRequired,
  // Handed the mutation's result once the API has taken the request.
  onDone: PropTypes.func.isRequired
}

export default TrashDialog
